import React, {useRef, useState} from 'react';
import {View, FlatList, TouchableOpacity, Image} from 'react-native';
import searchIcon from '../../Assets/Images/png/search.png';
import NoIssueSelected from '../../Assets/Images/Svgs/no_issue_selected.svg';
import {styles} from './style';
import Box from '@/Atoms/Box';
import {
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
} from '@expo/vector-icons';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import CCText from '@/Atoms/CCText';
import CCTextInput from '@/Atoms/CCTextInput';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {useNavigation} from '@react-navigation/native';
import CCButton from '@/Atoms/CCButton';
import {ScreenName} from '@/navigators/StackConstants';
import {useGetUserTickets} from '@/Hooks/GetSupportModalApi/useGetUserTickets';
import {formatDateToDDMMYYYY, formatTimeHHMM} from '@/Utils/CommonUtils';
import {useGetTicketDetails} from '@/Hooks/GetSupportModalApi/useGetTicketDetails';
import {useSendTicketMessage} from '@/Hooks/GetSupportModalApi/useSendTicketMessage';
import {Video} from 'expo-av';
import ChatList from './components/ChatList';
import ChatView, {Message} from './components/ChatView';
import {RAISE_A_TICKET} from '@/Utils/Constants';
import SupportModal from '@/Components/UserAssistance/component/SupportModal';

interface Ticket {
  id: string;
  title: string;
  date: string;
  time: string;
  status: 'Resolved' | 'In progress' | 'Not Seen';
  unreadCount?: number;
  messages: Message[];
}

const TicketListScreen = ({dashboardData}) => {
  const {isMobileOrTab} = useMediaQuery();
  const navigation = useNavigation();
  const flatListRef = useRef<FlatList>(null);
  const [search, setSearch] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [visible, setVisible] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'status'>('status');
  const [message, setMessage] = useState('');
  const [openModal, setOpenModal] = useState(false);

  const userKey = dashboardData?.user?.user_key;

  const {
    data: ticketsData = [],
    isLoading,
    isError,
  } = useGetUserTickets({
    userKey: userKey || '',
    // from_date: '',
    status: null,
  });

  const {
    data: ticketDetails,
    isLoading: isTicketLoading,
    isError: isTicketError,
    refetch: refetchTicketDetails,
  } = useGetTicketDetails(selectedTicketId || undefined, !!selectedTicketId);

  const {mutate: sendMessage, isLoading: isMessageLoading} =
    useSendTicketMessage(selectedTicketId);

  const handleSend = () => {
    if (!message && !file) return;
    if (!selectedTicketId) return;

    sendMessage(
      {user_key: userKey, message, file: file},
      {
        onSuccess: async res => {
          console.log('Message sent successfully', res);

          setMessage('');
          setFile(null);

          await refetchTicketDetails();

          setTimeout(
            () => flatListRef.current?.scrollToEnd({animated: true}),
            100,
          );
        },
        onError: err => {
          console.log('Failed to send message', err);
        },
      },
    );
  };

  const [file, setFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const messages = React.useMemo(() => {
    if (!ticketDetails?.comments) return [];

    return [...ticketDetails.comments].map(c => {
      let from: 'left' | 'right' = 'left';
      let user = 'S';

      switch (c.comment_type) {
        case 0:
        case 4:
          from = 'left';
          user = 'S';
          break;
        case 2:
          from = 'right';
          user = 'You';
          break;
      }

      return {
        id: String(c.id),
        text: c.comment,
        from,
        user,
        uploaded_file: c.uploaded_file,
        created_at: c.created_at,
      };
    });
  }, [ticketDetails]);
  console.log(ticketDetails?.comments, 'messages');

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      event.target.value = '';
      return;
    }

    const fileSizeMB = selectedFile.size / (1024 * 1024);
    const isValidFileType =
      selectedFile.type.startsWith('image') ||
      selectedFile.type.startsWith('video');
    const isValidSize = fileSizeMB <= 15;

    if (!isValidFileType || !isValidSize) {
      setFile(null);
      setErrorMessage('Invalid file type or exceeds 15MB size limit.');
    } else {
      setFile(selectedFile);
      setErrorMessage(null);
      console.log('Selected file:', selectedFile);
    }

    event.target.value = '';
  };

  const filteredTickets = ticketsData.filter(ticket =>
    ticket.title.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (type: 'date' | 'status') => {
    setSortBy(type);
    setVisible(false);
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Image
        source={NoIssueSelected}
        alt="No issue selected"
        style={{width: 180, height: 180, marginBottom: 20}}
      />
      <CCText
        fontSize={FontSizes[16]}
        color={CCColors?.TextColor?.Dark}
        style={styles.emptyText}>
        No issue selected
      </CCText>
      <CCText
        fontSize={FontSizes[14]}
        color={CCColors?.TextColor?.Dark}
        style={styles.emptySubText}>
        Select an issue from the left list
      </CCText>
    </View>
  );

  return (
    <View style={styles.container}>
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={12}>
        <Box flexDirection="row" gap={12}>
          <TouchableOpacity
            onPress={
              isMobileOrTab
                ? selectedTicket
                  ? () => setSelectedTicket(null)
                  : () => navigation.goBack()
                : navigation.canGoBack()
                ? () => navigation.goBack()
                : () => navigation.navigate(ScreenName?.Dashboard)
            }>
            <MaterialCommunityIcons
              name="arrow-left"
              size={24}
              color={CCColors.Primary.Dark}
              style={{top: isMobileOrTab ? 0 : 5}}
            />
          </TouchableOpacity>
          <CCText fontSize={FontSizes[18]}>Ticket List</CCText>
        </Box>
        <CCButton onPress={() => setOpenModal(true)}>
          <CCText fontSize={FontSizes[14]} color={CCColors?.White}>
            {RAISE_A_TICKET}
          </CCText>
        </CCButton>
      </Box>
      <View style={styles.innerContainer}>
        {isMobileOrTab ? (
          selectedTicket ? (
            <View style={styles.rightPanel}>
              <ChatView
                message={message}
                setMessage={setMessage}
                handleSend={handleSend}
                handleFileChange={handleFileChange}
                details={ticketDetails}
                file={file}
                setFile={setFile}
                flatListRef={flatListRef}
                isTicketList={true}
                messages={messages}
                refresh={false}
                ticket={selectedTicket}
              />
            </View>
          ) : (
            <ChatList
              search={search}
              setSearch={setSearch}
              visible={visible}
              setVisible={setVisible}
              sortBy={sortBy}
              handleSelect={handleSelect}
              ticketDetails={ticketDetails}
              filteredTickets={filteredTickets}
              selectedTicket={selectedTicket}
              setSelectedTicket={setSelectedTicket}
              setSelectedTicketId={setSelectedTicketId}
              showSearchBar={true}
              isTicketList={true}
            />
          )
        ) : (
          <>
            <ChatList
              search={search}
              setSearch={setSearch}
              visible={visible}
              setVisible={setVisible}
              sortBy={sortBy}
              handleSelect={handleSelect}
              ticketDetails={ticketDetails}
              filteredTickets={filteredTickets}
              selectedTicket={selectedTicket}
              setSelectedTicket={setSelectedTicket}
              setSelectedTicketId={setSelectedTicketId}
              showSearchBar={true}
              isTicketList={true}
            />
            <View
              style={[
                styles.rightPanel,
                {
                  justifyContent:
                    selectedTicketId && ticketDetails ? 'flex-start' : 'center',
                  alignItems:
                    selectedTicketId && ticketDetails ? 'flex-start' : 'center',
                },
              ]}>
              {selectedTicketId && ticketDetails ? (
                <ChatView
                  message={message}
                  setMessage={setMessage}
                  handleSend={handleSend}
                  handleFileChange={handleFileChange}
                  details={ticketDetails}
                  file={file}
                  setFile={setFile}
                  flatListRef={flatListRef}
                  messages={messages}
                  isTicketList={true}
                  refresh={false}
                  ticket={selectedTicket}
                />
              ) : (
                renderEmptyState()
              )}
            </View>
          </>
        )}
      </View>
      <SupportModal
        openModal={openModal}
        handleClose={() => setOpenModal(false)}
      />
    </View>
  );
};

export default TicketListScreen;
